import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe, JsonPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AdminFeedbackService } from '../../../core/feedback/admin-feedback.service';
import {
  AdminFeedbackReport,
  TriageEvent,
  TriageStatus,
} from '../../../core/feedback/feedback.types';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/** The statuses a triager can set, in the order they usually happen. */
const TRIAGE_CHOICES: readonly { value: TriageStatus; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'TRIAGED', label: 'Triaged' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'NEEDS_DISCUSSION', label: 'Needs discussion' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'WONT_FIX', label: "Won't fix" },
  { value: 'DUPLICATE', label: 'Duplicate' },
];

/**
 * One report, its full context, and the controls to triage it.
 *
 * The status history below the form is append-only (N8): every decision stays
 * on record with who made it and why, and nothing here edits or removes a past
 * one — setting a new status appends to the trail.
 */
@Component({
  selector: 'app-feedback-detail',
  imports: [
    RouterLink,
    DatePipe,
    JsonPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    IconComponent,
    StatusChipComponent,
    DashPipe,
  ],
  templateUrl: './feedback-detail.html',
  styleUrl: './feedback-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackDetailComponent implements OnInit {
  private readonly service = inject(AdminFeedbackService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  private readonly route = inject(ActivatedRoute);

  /** The report's short_id, read from the route (see ai-evaluation-detail). */
  protected readonly id = signal('');

  protected readonly report = signal<AdminFeedbackReport | null>(null);
  protected readonly events = signal<TriageEvent[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly saving = signal(false);
  protected readonly saveError = signal<string | null>(null);

  /** A short-lived link to the screenshot, fetched on demand. */
  protected readonly screenshotUrl = signal<string | null>(null);
  protected readonly loadingScreenshot = signal(false);

  protected readonly choices = TRIAGE_CHOICES;

  protected readonly form = this.fb.nonNullable.group({
    to_status: ['NEW' as TriageStatus],
    note: [''],
  });

  ngOnInit(): void {
    this.id.set(this.route.snapshot.paramMap.get('id') ?? '');
    this.load();
  }

  protected retry(): void {
    this.load();
  }

  /** Record the chosen status. The trail is re-read, never patched locally. */
  protected save(): void {
    const current = this.report();
    if (!current || this.saving()) return;

    this.saving.set(true);
    this.saveError.set(null);

    const { to_status, note } = this.form.getRawValue();
    this.service
      .retriage(current.short_id, to_status, note)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.apply(detail.report, detail.triage_events);
          this.form.controls.note.setValue('');
          this.saving.set(false);
          this.snackBar.open(
            `Status set to ${detail.report.triage_status_display}.`,
            'Dismiss',
            { duration: 4000 },
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.saving.set(false);
          this.saveError.set("That didn't save. Please try again.");
          this.cdr.markForCheck();
        },
      });
  }

  /** Fetch a time-limited link and open the screenshot in a new tab. */
  protected viewScreenshot(): void {
    const current = this.report();
    if (!current || this.loadingScreenshot()) return;

    this.loadingScreenshot.set(true);
    this.service
      .screenshotUrl(current.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ url }) => {
          this.screenshotUrl.set(url);
          this.loadingScreenshot.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingScreenshot.set(false);
          this.saveError.set('The screenshot could not be loaded.');
          this.cdr.markForCheck();
        },
      });
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);

    this.service
      .get(this.id())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.apply(detail.report, detail.triage_events);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private apply(report: AdminFeedbackReport, events: TriageEvent[]): void {
    this.report.set(report);
    this.events.set(events);
    // Default the picker to where the report actually stands, so the form
    // reflects the server's view rather than a stale choice.
    this.form.controls.to_status.setValue(report.triage_status);
  }
}
